import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  FormHelperText,
  ButtonGroup,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Papa from 'papaparse';
import intl from 'components/i18n/ReactIntlWrapper';
import {
  useMovementOperations,
  BulkImportRowRequest,
} from 'store/registry/useMovementOperations';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';

interface CsvImportDialogProps {
  open: boolean;
  accountId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const DATE_FORMATS = [
  { label: 'YYYY-MM-DD (ISO)', value: 'yyyy-MM-dd' },
  { label: 'MM/DD/YYYY (US)', value: 'MM/dd/yyyy' },
  { label: 'DD/MM/YYYY (EU)', value: 'dd/MM/yyyy' },
  { label: 'DD-MM-YYYY', value: 'dd-MM-yyyy' },
  { label: 'MM-DD-YYYY', value: 'MM-dd-yyyy' },
  { label: 'YYYY/MM/DD', value: 'yyyy/MM/dd' },
  { label: 'DD.MM.YYYY', value: 'dd.MM.yyyy' },
  { label: 'M/D/YYYY', value: 'M/d/yyyy' },
];

type ColumnMapping = {
  amount: string;
  description: string;
  date: string;
};

interface PreviewRow {
  date: string;
  amount: number;
  description: string;
  direction: 'credit' | 'debit';
}

const STEPS = [
  'csvImport.step.upload',
  'csvImport.step.map',
  'csvImport.step.preview',
];

const NONE = '__none__';

const CsvImportDialog: React.FC<CsvImportDialogProps> = ({
  open,
  accountId,
  onClose,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState<ColumnMapping>({ amount: '', description: '', date: '' });
  const [dateFormat, setDateFormat] = useState('yyyy-MM-dd');
  const [mappingErrors, setMappingErrors] = useState<Partial<ColumnMapping>>({});
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [parseError, setParseError] = useState('');
  const [importResult, setImportResult] = useState<{ imported: number; failed: number; errors: string[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { bulkImportMovements } = useMovementOperations();
  const { success: showSuccess, error: showError } = useToastMessageQueue();

  const resetState = useCallback(() => {
    setActiveStep(0);
    setCsvHeaders([]);
    setCsvRows([]);
    setFileName('');
    setMapping({ amount: '', description: '', date: '' });
    setDateFormat('yyyy-MM-dd');
    setMappingErrors({});
    setPreviewRows([]);
    setParseError('');
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // ── Step 1: File upload ──────────────────────────────────────────────────

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError('');
    setFileName(file.name);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setParseError(intl.get('csvImport.error.parseFailed'));
          return;
        }
        const headers = results.meta.fields ?? [];
        if (headers.length === 0) {
          setParseError(intl.get('csvImport.error.noColumns'));
          return;
        }
        setCsvHeaders(headers);
        setCsvRows(results.data);
        setMapping({ amount: '', description: '', date: '' });
        setActiveStep(1);
      },
      error: () => {
        setParseError(intl.get('csvImport.error.parseFailed'));
      },
    });
  }, []);

  const handleDropZoneClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      const fakeEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(fakeEvent);
    },
    [handleFileChange]
  );

  // ── Step 2: Column mapping ───────────────────────────────────────────────

  const validateMapping = useCallback((): boolean => {
    const errors: Partial<ColumnMapping> = {};
    if (!mapping.amount) errors.amount = intl.get('csvImport.error.fieldRequired');
    if (!mapping.description) errors.description = intl.get('csvImport.error.fieldRequired');
    if (!mapping.date) errors.date = intl.get('csvImport.error.fieldRequired');
    setMappingErrors(errors);
    return Object.keys(errors).length === 0;
  }, [mapping]);

  // Parse a date string according to the selected format (client-side preview only)
  const parseDate = useCallback(
    (raw: string): Date | null => {
      if (!raw) return null;
      // Map our format tokens to known positions
      const fmt = dateFormat;
      let year = 0, month = 0, day = 0;
      const r = raw.trim();
      // Try to derive separators and positions from the format
      try {
        const sep = fmt.match(/[^yMd]/)?.[0] ?? '-';
        const fParts = fmt.split(sep);
        const rParts = r.split(sep.replace('/', '/'));
        if (fParts.length !== 3 || rParts.length !== 3) return null;
        fParts.forEach((part, i) => {
          const val = parseInt(rParts[i], 10);
          if (part.toLowerCase().startsWith('y')) year = val;
          else if (part.startsWith('M')) month = val;
          else if (part.startsWith('d')) day = val;
        });
        const d = new Date(year, month - 1, day);
        return isNaN(d.getTime()) ? null : d;
      } catch {
        return null;
      }
    },
    [dateFormat]
  );

  const buildPreview = useCallback((): boolean => {
    if (!validateMapping()) return false;

    const rows: PreviewRow[] = [];
    for (const csvRow of csvRows) {
      const rawAmount = csvRow[mapping.amount]?.replace(',', '.');
      const rawDate = csvRow[mapping.date];
      const description = csvRow[mapping.description]?.trim();
      const amount = parseFloat(rawAmount);
      if (isNaN(amount) || !rawDate || !description) continue;
      rows.push({
        date: rawDate.trim(),
        amount,
        description,
        direction: amount >= 0 ? 'credit' : 'debit',
      });
    }
    setPreviewRows(rows);
    setActiveStep(2);
    return true;
  }, [validateMapping, csvRows, mapping, parseDate]);

  // ── Step 3: Preview & submit ─────────────────────────────────────────────

  const toggleRowDirection = useCallback((index: number) => {
    setPreviewRows((rows) =>
      rows.map((r, i) =>
        i === index
          ? { ...r, direction: r.direction === 'credit' ? 'debit' : 'credit' }
          : r
      )
    );
  }, []);

  const setAllDirections = useCallback((direction: 'credit' | 'debit') => {
    setPreviewRows((rows) => rows.map((r) => ({ ...r, direction })));
  }, []);

  const handleImport = useCallback(async () => {
    const rows: BulkImportRowRequest[] = previewRows.map((r) => ({
      date: r.date,
      amount: r.direction === 'debit' ? -Math.abs(r.amount) : Math.abs(r.amount),
      description: r.description,
      dateFormat,
    }));

    try {
      const result = await bulkImportMovements.mutateAsync({ idAccount: accountId, rows });
      setImportResult(result);
      setActiveStep(3);
      if (result.imported > 0) {
        showSuccess({
          message: intl.get('csvImport.success', { count: result.imported }),
        });
        onSuccess?.();
      }
    } catch {
      showError({ message: intl.get('csvImport.error.importFailed') });
    }
  }, [previewRows, dateFormat, bulkImportMovements, accountId, showSuccess, showError, onSuccess]);

  // ── Render helpers ───────────────────────────────────────────────────────

  const renderUploadStep = () => (
    <Box>
      <Box
        onClick={handleDropZoneClick}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          border: '2px dashed',
          borderColor: 'primary.main',
          borderRadius: 2,
          p: 5,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'background 0.2s',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Icon icon="mdi:file-upload-outline" width={48} color="#1976d2" />
        <Typography variant="h6" mt={1}>
          {intl.get('csvImport.dropzone.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {intl.get('csvImport.dropzone.subtitle')}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
      {parseError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {parseError}
        </Alert>
      )}
    </Box>
  );

  const renderMappingStep = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="body2" color="text.secondary">
        {intl.get('csvImport.mapping.hint', { file: fileName, rows: csvRows.length })}
      </Typography>

      <Box display="flex" flexDirection="column" gap={2}>
        {/* Amount column */}
        <FormControl error={!!mappingErrors.amount} fullWidth>
          <InputLabel>{intl.get('csvImport.mapping.amountColumn')}</InputLabel>
          <Select
            value={mapping.amount}
            label={intl.get('csvImport.mapping.amountColumn')}
            onChange={(e) => setMapping((m) => ({ ...m, amount: e.target.value }))}
          >
            <MenuItem value="">{intl.get('csvImport.mapping.selectColumn')}</MenuItem>
            {csvHeaders.map((h) => (
              <MenuItem key={h} value={h}>{h}</MenuItem>
            ))}
          </Select>
          {mappingErrors.amount && <FormHelperText>{mappingErrors.amount}</FormHelperText>}
        </FormControl>

        {/* Description column */}
        <FormControl error={!!mappingErrors.description} fullWidth>
          <InputLabel>{intl.get('csvImport.mapping.descriptionColumn')}</InputLabel>
          <Select
            value={mapping.description}
            label={intl.get('csvImport.mapping.descriptionColumn')}
            onChange={(e) => setMapping((m) => ({ ...m, description: e.target.value }))}
          >
            <MenuItem value="">{intl.get('csvImport.mapping.selectColumn')}</MenuItem>
            {csvHeaders.map((h) => (
              <MenuItem key={h} value={h}>{h}</MenuItem>
            ))}
          </Select>
          {mappingErrors.description && <FormHelperText>{mappingErrors.description}</FormHelperText>}
        </FormControl>

        {/* Date column + format */}
        <Box display="flex" gap={2}>
          <FormControl error={!!mappingErrors.date} fullWidth>
            <InputLabel>{intl.get('csvImport.mapping.dateColumn')}</InputLabel>
            <Select
              value={mapping.date}
              label={intl.get('csvImport.mapping.dateColumn')}
              onChange={(e) => setMapping((m) => ({ ...m, date: e.target.value }))}
            >
              <MenuItem value="">{intl.get('csvImport.mapping.selectColumn')}</MenuItem>
              {csvHeaders.map((h) => (
                <MenuItem key={h} value={h}>{h}</MenuItem>
              ))}
            </Select>
            {mappingErrors.date && <FormHelperText>{mappingErrors.date}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{intl.get('csvImport.mapping.dateFormat')}</InputLabel>
            <Select
              value={dateFormat}
              label={intl.get('csvImport.mapping.dateFormat')}
              onChange={(e) => setDateFormat(e.target.value)}
            >
              {DATE_FORMATS.map((f) => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Sample data preview */}
      {csvHeaders.length > 0 && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            {intl.get('csvImport.mapping.sampleData')}
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {csvHeaders.map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 'bold', py: 0.5 }}>
                      {h}
                      {mapping.amount === h && (
                        <Chip label={intl.get('csvImport.mapping.amount')} size="small" color="primary" sx={{ ml: 0.5 }} />
                      )}
                      {mapping.description === h && (
                        <Chip label={intl.get('csvImport.mapping.description')} size="small" color="secondary" sx={{ ml: 0.5 }} />
                      )}
                      {mapping.date === h && (
                        <Chip label={intl.get('csvImport.mapping.date')} size="small" color="success" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {csvRows.slice(0, 3).map((row, i) => (
                  <TableRow key={i}>
                    {csvHeaders.map((h) => (
                      <TableCell key={h} sx={{ py: 0.5 }}>{row[h] ?? ''}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );

  const renderPreviewStep = () => (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" gap={1}>
          <Chip
            icon={<Icon icon="mdi:check-circle" width={16} />}
            label={intl.get('csvImport.preview.validRows', { count: previewRows.length })}
            color="success"
            variant="outlined"
          />
          {csvRows.length - previewRows.length > 0 && (
            <Chip
              icon={<Icon icon="mdi:alert-circle" width={16} />}
              label={intl.get('csvImport.preview.skippedRows', { count: csvRows.length - previewRows.length })}
              color="warning"
              variant="outlined"
            />
          )}
        </Box>

        {/* Bulk flip controls */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {intl.get('csvImport.preview.setAll')}:
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            <Button
              color="success"
              onClick={() => setAllDirections('credit')}
              startIcon={<Icon icon="mdi:arrow-down" width={14} />}
            >
              {intl.get('csvImport.preview.credit')}
            </Button>
            <Button
              color="error"
              onClick={() => setAllDirections('debit')}
              startIcon={<Icon icon="mdi:arrow-up" width={14} />}
            >
              {intl.get('csvImport.preview.debit')}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{intl.get('movements.date')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{intl.get('accountManagement.description')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{intl.get('accountManagement.amount')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">{intl.get('csvImport.preview.direction')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row, i) => (
              <TableRow key={i}>
                <TableCell sx={{ fontFamily: 'monospace', py: 0.5 }}>{row.date}</TableCell>
                <TableCell sx={{ py: 0.5 }}>{row.description}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', py: 0.5 }}>
                  <Typography
                    variant="body2"
                    color={row.direction === 'credit' ? 'success.main' : 'error.main'}
                    fontWeight="600"
                  >
                    {Math.abs(row.amount).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  <Tooltip title={intl.get('csvImport.preview.toggleDirection')}>
                    <Chip
                      label={
                        row.direction === 'credit'
                          ? intl.get('csvImport.preview.credit')
                          : intl.get('csvImport.preview.debit')
                      }
                      icon={
                        <Icon
                          icon={row.direction === 'credit' ? 'mdi:arrow-down' : 'mdi:arrow-up'}
                          width={14}
                        />
                      }
                      color={row.direction === 'credit' ? 'success' : 'error'}
                      size="small"
                      onClick={() => toggleRowDirection(i)}
                      sx={{ cursor: 'pointer', fontWeight: 600 }}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {intl.get('csvImport.preview.directionHint')}
      </Typography>
    </Box>
  );

  const renderResultStep = () => (
    <Box display="flex" flexDirection="column" gap={2} alignItems="center" py={2}>
      {importResult && (
        <>
          {importResult.imported > 0 && (
            <Box textAlign="center">
              <Icon icon="mdi:check-circle" width={64} color="#4caf50" />
              <Typography variant="h6" mt={1}>
                {intl.get('csvImport.result.success', { count: importResult.imported })}
              </Typography>
            </Box>
          )}
          {importResult.failed > 0 && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              <Typography variant="body2" fontWeight="bold" mb={0.5}>
                {intl.get('csvImport.result.failed', { count: importResult.failed })}
              </Typography>
              {importResult.errors.map((e, i) => (
                <Typography key={i} variant="caption" display="block">• {e}</Typography>
              ))}
            </Alert>
          )}
        </>
      )}
    </Box>
  );

  const isLastStep = activeStep === 3;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:file-import-outline" width={24} />
            {intl.get('csvImport.title')}
          </Box>
          <Tooltip title={intl.get('common.close')}>
            <IconButton size="small" onClick={handleClose}>
              <Icon icon="mdi:close" width={20} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        {activeStep < 3 && (
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {STEPS.map((labelKey) => (
              <Step key={labelKey}>
                <StepLabel>{intl.get(labelKey)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Step content */}
        <Box>
          {activeStep === 0 && renderUploadStep()}
          {activeStep === 1 && renderMappingStep()}
          {activeStep === 2 && renderPreviewStep()}
          {activeStep === 3 && renderResultStep()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!isLastStep && (
          <Button
            onClick={handleClose}
            disabled={bulkImportMovements.isPending}
          >
            {intl.get('common.cancel')}
          </Button>
        )}

        {activeStep === 1 && (
          <Button
            variant="outlined"
            onClick={() => setActiveStep(0)}
            startIcon={<Icon icon="mdi:arrow-left" width={18} />}
          >
            {intl.get('common.back')}
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            variant="outlined"
            onClick={() => setActiveStep(1)}
            startIcon={<Icon icon="mdi:arrow-left" width={18} />}
          >
            {intl.get('common.back')}
          </Button>
        )}

        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={() => buildPreview()}
            endIcon={<Icon icon="mdi:arrow-right" width={18} />}
          >
            {intl.get('csvImport.action.preview')}
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={bulkImportMovements.isPending || previewRows.length === 0}
            startIcon={
              bulkImportMovements.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <Icon icon="mdi:upload" width={18} />
              )
            }
          >
            {intl.get('csvImport.action.import', { count: previewRows.length })}
          </Button>
        )}

        {isLastStep && (
          <Button variant="contained" onClick={handleClose}>
            {intl.get('common.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CsvImportDialog;
